import { createContext, useContext } from 'react';
import { STORE_MAX } from './winners';

/*
 * English and French for the Système U pages (dashboard and winners).
 *
 * Only the words change with the language. Every figure stays French-formatted
 * in both ("412 318", "77,3 %", "6 min 04 s"): the audience is a French
 * retailer, and the design writes them that way under English labels too.
 *
 * `SurveyStrings` is the English dictionary's type, so a key missing from the
 * French one — or a function taking other arguments — fails the type check
 * instead of rendering `undefined`. French follows its own typography: a
 * no-break space before ":" and "%", typographic apostrophes.
 */

export type Lang = 'en' | 'fr';

export const LANGS: readonly Lang[] = ['en', 'fr'];

/** Each language named in itself — the toggle's accessible labels. */
export const LANG_NAMES: Record<Lang, string> = { en: 'English', fr: 'Français' };

const NBSP = ' ';

const en = {
  // Header
  subtitle: 'Real-time dashboard',
  noLiveData: 'No live data',
  live: 'Live',
  notLive: 'Not live',
  updatedAt: (time: string) => `· updated at ${time}`,
  period: 'Period',
  /** Picker: the whole campaign. */
  scopeAll: (seasons: number, from: string, to: string) => `${seasons === 2 ? 'Both seasons' : 'All seasons'} · ${from} → ${to}`,
  /** Picker: one season, by its own weeks. */
  scopeSeason: (n: number, from: number, to: number) => `Season ${n} · Week ${from} → Week ${to}`,
  exportLabel: 'Export',
  report: 'Report',
  reportPlaceholder: 'Report…',
  reportsActive: 'Active',
  reportsArchived: 'Archived',
  users: 'Users',
  signOut: 'Sign out',
  winnersLink: 'Winners & prizes →',
  language: 'Language',

  // Seasons, weeks and where the campaign stands
  week: (n: number) => `Week ${n}`,
  inProgress: ' (in progress)',
  pct: (n: number) => `${n}%`,
  season: (n: number) => `Season ${n}`,
  seasonShort: (n: number) => `S${n}`,
  /** A week where both seasons sit side by side: "S2 · Week 1". */
  seasonWeek: (s: number, n: number) => `S${s} · Week ${n}`,
  seasonBadge: {
    ended: (n: number) => `S${n} · ENDED`,
    week: (n: number, k: number, total: number) => `S${n} · WEEK ${k} / ${total}`,
    starts: (n: number, date: string) => `S${n} · STARTS ${date}`,
  },
  notStartedYet: 'Not started yet',
  and: ' and ',

  // Campaign calendar
  calendarTitle: 'Campaign calendar',
  today: (date: string) => `Today: ${date}`,
  todayShort: (date: string) => `Today · ${date}`,
  campaignEndedOn: (date: string) => `Campaign ended · ${date}`,
  seasonStarts: (date: string, n: number) => `${date} · Season ${n} starts`,
  weekDone: (n: number) => `Season ${n} week done`,
  weekInProgress: 'Week in progress',
  upcoming: 'Upcoming',
  calendarLabel: 'Campaign calendar: the weeks of each season and where the campaign stands',

  // Visitors funnel
  uniqueVisitorsEyebrow: 'UNIQUE VISITORS',
  funnelNote: 'conversion funnel from first visit to playing the quiz',
  funnelNoteAll: (both: string | null) =>
    `conversion funnel from first visit to playing the quiz · campaign total, people who visited in both seasons count once${both ? ` (${both} in both)` : ''}`,
  funnelNotStarted: '— · not started yet',
  uniqueVisitors: 'Unique visitors',
  optIns: 'Opt-ins (T&Cs accepted)',
  startedAQuiz: 'Started a quiz',
  uniquePlayers: 'Unique players',
  funnelTip: (label: string, value: string, pct: string) => `${label}: ${value} (${pct} of unique visitors)`,

  // Lottery tickets
  ticketsEyebrow: 'LOTTERY TICKETS ISSUED',
  ticketsPerPlayer: (x: string) => `${x} tickets per player`,
  ticketsTip: (week: string, n: string) => `${week}: ${n} lottery tickets issued`,
  weekInProgressNote: (week: string) => `${week} in progress (shown in red)`,
  seasonTickets: (n: string) => `${n} tickets`,
  allWeeksComplete: 'Campaign ended · all weeks complete',

  // KPI tiles
  ofUniqueVisitors: (pct: string) => `${pct} of unique visitors`,
  playsKpi: 'Plays (quizzes completed)',
  playsPerPlayerMax: (x: string, max: number) => `${x} plays per player · max ${max} per week`,
  totalTime: 'Total time in the app',
  totalTimeSub: 'all users, all sessions',
  avgTime: 'Average time per user',
  avgTimeSub: 'per unique player, whole campaign',
  avgTimeSubSeason: (n: number) => `per unique player, whole Season ${n}`,

  // Quizzes started and completed
  completionTitle: 'Quizzes started and completed',
  completionRateNote: 'of the quizzes started were completed',
  quizzes: 'Quizzes',
  players: 'Players',
  started: 'Started',
  completed: 'Completed',
  notCompleted: 'Not completed',
  neverCompletedTip: 'Players who started a quiz but never completed one',
  byWeek: 'By week',
  completedPct: (pct: string) => `${pct} completed`,
  completionTip: (week: string, completed: string, started: string, pct: string) =>
    `${week}: ${completed} of ${started} quizzes started were completed (${pct})`,
  notCompletedTip: (week: string, n: string) => `${week}: ${n} quizzes started but not completed`,
  completionNote: 'A quiz counts as started when its session opens — each one uses an attempt. Not completed = started − completed.',
  startedUnavailable: "Quizzes started come from the platform's daily attempt counters — not available yet.",

  // Per day
  /** Locale for written-out dates ("Friday 30 October 2026", "6 Oct"). */
  locale: 'en-GB',
  dailyTitle: 'Daily unique visitors',
  dailyLatest: (day: string, visitors: string) => `${day}: ${visitors} unique visitors`,
  dailyNote: "A visitor who comes back on another day counts again on that day, so the days add up to more than the campaign's unique visitors.",
  dailyEmpty: 'The daily figures appear after the next pipeline run.',
  dailyChartLabel: 'Daily unique visitors — use the arrow keys to read each day',
  dailyShowTable: 'Show the figures per day',
  day: 'Day',
  seasonCol: 'Season',
  /** Axis label for a campaign week too narrow for the full "Week 4". */
  weekShort: (n: number) => `Wk ${n}`,

  // Marketing consents
  consentsTitle: 'Marketing consents',
  totalConsents: 'total consents',
  totalConsentsAll: 'total consents · both seasons',
  consentsNote: "A season counts the consents given during it; the campaign total counts each member's latest choice once.",
  emailOnly: 'Email only',
  emailAndSms: 'Email + SMS',
  smsOnly: 'SMS only',
  consentTip: (label: string, n: string, pct: string) => `${label}: ${n} members (${pct} of consents)`,
  emailConsents: 'Email consents',
  smsConsents: 'SMS consents',
  ofOptIns: (pct: string) => `${pct} of opt-ins`,

  // Unique players per week
  playersPerWeek: 'Unique players per week',
  notStartedTip: (week: string) => `${week} has not started yet`,
  playersTip: (week: string, n: string) => `${week}: ${n} players completed at least one quiz`,
  playersNote: (n: string) => `Players who completed at least one quiz that week · ${n} unique players over the campaign (both seasons, counted once)`,
  playersNoteSeason: (n: string, s: number) => `Players who completed at least one quiz that week · ${n} unique players in Season ${s}`,
  seasonPlayers: (s: number, n: string) => `Season ${s} · ${n} unique players`,

  // Plays per user — weekly distribution
  playsDistribution: 'Plays per user — weekly distribution',
  plays: (n: number) => `${n} ${n === 1 ? 'play' : 'plays'}`,
  weeklyMaximum: ' (weekly maximum)',
  playedTimesTip: (week: string, pct: string, n: number) => `${week}: ${pct} of the week's players played ${n} ${n === 1 ? 'time' : 'times'}`,
  playsPerUser: (x: string) => `${x} plays / user`,
  distributionNote: (max: number) =>
    `Share of that week's unique players by number of quizzes played in the week · each user can play up to ${max} times per week`,

  // Time in the app
  timePerWeek: 'Time spent in the app per week',
  noTimeTip: (week: string) => `${week}: no time measured`,
  timeTip: (week: string, hours: string, perPlayer: string) => `${week}: ${hours} in the app, ${perPlayer} per player`,
  timeUnavailable: "Time in the app comes from the app's analytics events — not available yet.",
  totalTimeInApp: 'Total time in app',
  avgTimeCampaign: 'Avg. time per user (campaign)',
  perUser: (week: string) => `${week} · per user`,
  seasonTime: (n: number) => `Season ${n} · time in app`,
  avgTimeSeason: (n: number) => `Avg. time per user (Season ${n})`,

  // Weekly summary
  weeklySummary: 'Weekly summary',
  col: {
    week: 'Week',
    uniquePlayers: 'Unique players',
    started: 'Quizzes started',
    plays: 'Plays',
    completion: 'Completion',
    playsPerUser: 'Plays / user',
    tickets: 'Tickets issued',
    timeInApp: 'Time in app',
    timePerUser: 'Time / user',
  },
  campaignTotal: 'Campaign total',
  seasonTotal: (n: number) => `Season ${n} total`,
  weeklySumNote:
    'Weekly unique players are counted per week, so their sum is higher than the season total · the campaign total counts people who played in both seasons once',

  // CSV exports (opened in Excel: plain headers, the decimal mark of the language)
  csvDecimal: '.',
  csvTotal: 'Total',
  csvWeekly: {
    season: 'Season',
    week: 'Week',
    start: 'Start',
    end: 'End',
    status: 'Status',
    uniquePlayers: 'Unique players',
    playersStarted: 'Players started',
    quizzesStarted: 'Quizzes started',
    plays: 'Plays',
    notCompleted: 'Not completed',
    playsPerUser: 'Plays per user',
    tickets: 'Tickets issued',
    timeInAppHours: 'Time in app (h)',
    timePerUserSec: 'Time per user (s)',
  },
  weekStatus: { done: 'Done', live: 'In progress', planned: 'Planned' },
  csvWinners: {
    week: 'Week',
    drawDate: 'Draw date',
    store: 'Store address',
    name: 'Display name',
    card: 'Card number',
    prize: 'Prize (€)',
    status: 'Status',
  },
  winnerStatus: { pending: 'Pending', approved: 'Approved', no_winner: 'No winner' },

  // Winners & prizes
  winnersSubtitle: 'Winners & prizes · weekly lottery',
  sampleData: 'Sample data',
  backToDashboard: '← Back to dashboard',
  winnersTitle: 'Winners and prizes to hand out',
  winnersNote: 'Weekly lottery draw · one winner per week · prize: €500 credit',
  exportCsv: 'Export to CSV',
  winnersLoadError: 'The winners could not be loaded. Try again in a moment.',
  loadingWinners: 'Loading winners…',
  noDrawYet: 'No draw yet — the first winner is drawn at the end of week 1.',
  winnersCol: {
    weekDate: 'Week & date',
    store: 'Store address',
    name: 'Name',
    card: 'Card number',
    prize: 'Prize',
    status: 'Status',
  },
  wk: (n: number) => `WK ${n}`,
  seasonWk: (s: number, n: number) => `S${s} · WK ${n}`,
  missingInfo: 'Missing information',
  approved: 'Approved',
  noWinner: 'No winner — nobody had a ticket',
  prizeCredit: (amount: string) => `€${amount} credit`,
  approveWarning: 'Approving publishes the name and the store address to every player of the campaign. They cannot be changed afterwards.',
  provideAndApprove: 'Provide information and approve',
  saveError: 'The winner could not be saved. Check the connection and try again.',
  dialogMeta: (wk: string, date: string, card: string) => `${wk} · ${date} · card ${card}`,
  storeAddress: 'Store address',
  storePlaceholder: 'e.g. Super U, 14 rue des Lilas, 44300 Nantes',
  displayName: 'Display name',
  namePlaceholder: 'e.g. Camille M.',
  nameHint: 'First name and the initial of the surname',
  cancel: 'Cancel',
  saving: 'Saving…',
  saveAndApprove: 'Save and approve',
  approvalError: {
    storeRequired: 'The store address is required.',
    storeTooLong: `The store address can be at most ${STORE_MAX} characters.`,
    storeChars: "Use letters, digits, spaces and . , ; : ! ? ° ' ( ) / - only (no “·”).",
    nameRequired: 'The display name is required.',
    nameFormat: 'Use the first name and the initial of the surname, e.g. Camille M.',
  },
};

export type SurveyStrings = typeof en;

const fr: SurveyStrings = {
  // En-tête
  subtitle: 'Tableau de bord en temps réel',
  noLiveData: 'Pas de données en direct',
  live: 'En direct',
  notLive: 'Pas en direct',
  updatedAt: (time) => `· mis à jour à ${time}`,
  period: 'Période',
  scopeAll: (seasons, from, to) => `${seasons === 2 ? 'Les deux saisons' : 'Toutes les saisons'} · ${from} → ${to}`,
  scopeSeason: (n, from, to) => `Saison ${n} · Semaine ${from} → Semaine ${to}`,
  exportLabel: 'Exporter',
  report: 'Rapport',
  reportPlaceholder: 'Rapport…',
  reportsActive: 'En cours',
  reportsArchived: 'Archivés',
  users: 'Utilisateurs',
  signOut: 'Déconnexion',
  winnersLink: 'Gagnants et lots →',
  language: 'Langue',

  // Saisons, semaines et état de la campagne
  week: (n) => `Semaine ${n}`,
  inProgress: ' (en cours)',
  pct: (n) => `${n}${NBSP}%`,
  season: (n) => `Saison ${n}`,
  seasonShort: (n) => `S${n}`,
  seasonWeek: (s, n) => `S${s} · Semaine ${n}`,
  seasonBadge: {
    ended: (n) => `S${n} · TERMINÉE`,
    week: (n, k, total) => `S${n} · SEMAINE ${k} / ${total}`,
    starts: (n, date) => `S${n} · DÉBUT LE ${date}`,
  },
  notStartedYet: 'Pas encore commencée',
  and: ' et ',

  // Calendrier de la campagne
  calendarTitle: 'Calendrier de la campagne',
  today: (date) => `Aujourd’hui${NBSP}: ${date}`,
  todayShort: (date) => `Aujourd’hui · ${date}`,
  campaignEndedOn: (date) => `Campagne terminée · ${date}`,
  seasonStarts: (date, n) => `${date} · début de la saison ${n}`,
  weekDone: (n) => `Semaine terminée · saison ${n}`,
  weekInProgress: 'Semaine en cours',
  upcoming: 'À venir',
  calendarLabel: `Calendrier de la campagne${NBSP}: les semaines de chaque saison et l’état de la campagne`,

  // Entonnoir des visiteurs
  uniqueVisitorsEyebrow: 'VISITEURS UNIQUES',
  funnelNote: 'tunnel de conversion, de la première visite à la participation au quiz',
  funnelNoteAll: (both) =>
    `tunnel de conversion, de la première visite à la participation au quiz · total campagne, une personne venue pendant les deux saisons compte une fois${both ? ` (${both} pendant les deux)` : ''}`,
  funnelNotStarted: '— · pas encore commencée',
  uniqueVisitors: 'Visiteurs uniques',
  optIns: 'Inscriptions (CGU acceptées)',
  startedAQuiz: 'Ont commencé un quiz',
  uniquePlayers: 'Joueurs uniques',
  funnelTip: (label, value, pct) => `${label}${NBSP}: ${value} (${pct} des visiteurs uniques)`,

  // Tickets de tirage
  ticketsEyebrow: 'TICKETS DE TIRAGE ÉMIS',
  ticketsPerPlayer: (x) => `${x} tickets par joueur`,
  ticketsTip: (week, n) => `${week}${NBSP}: ${n} tickets de tirage émis`,
  weekInProgressNote: (week) => `${week} en cours (en rouge)`,
  seasonTickets: (n) => `${n} tickets`,
  allWeeksComplete: 'Campagne terminée · toutes les semaines sont complètes',

  // Indicateurs clés
  ofUniqueVisitors: (pct) => `${pct} des visiteurs uniques`,
  playsKpi: 'Participations (quiz terminés)',
  playsPerPlayerMax: (x, max) => `${x} participations par joueur · ${max} max. par semaine`,
  totalTime: 'Temps total passé dans l’application',
  totalTimeSub: 'tous les utilisateurs, toutes les sessions',
  avgTime: 'Temps moyen par utilisateur',
  avgTimeSub: 'par joueur unique, sur toute la campagne',
  avgTimeSubSeason: (n) => `par joueur unique, sur toute la saison ${n}`,

  // Quiz commencés et terminés
  completionTitle: 'Quiz commencés et terminés',
  completionRateNote: 'des quiz commencés ont été terminés',
  quizzes: 'Quiz',
  players: 'Joueurs',
  started: 'Commencés',
  completed: 'Terminés',
  notCompleted: 'Non terminés',
  neverCompletedTip: 'Joueurs ayant commencé un quiz sans jamais en terminer un',
  byWeek: 'Par semaine',
  completedPct: (pct) => `${pct} terminés`,
  completionTip: (week, completed, started, pct) => `${week}${NBSP}: ${completed} des ${started} quiz commencés ont été terminés (${pct})`,
  notCompletedTip: (week, n) => `${week}${NBSP}: ${n} quiz commencés mais non terminés`,
  completionNote: `Un quiz est compté comme commencé à l’ouverture de sa session — chacune utilise une tentative. Non terminés = commencés − terminés.`,
  startedUnavailable: 'Les quiz commencés proviennent des compteurs quotidiens de tentatives de la plateforme — pas encore disponible.',

  // Par jour
  locale: 'fr-FR',
  dailyTitle: 'Visiteurs uniques par jour',
  dailyLatest: (day, visitors) => `${day}${NBSP}: ${visitors} visiteurs uniques`,
  dailyNote:
    'Un visiteur qui revient un autre jour compte à nouveau ce jour-là, la somme des jours dépasse donc le nombre de visiteurs uniques de la campagne.',
  dailyEmpty: 'Les chiffres quotidiens apparaîtront après la prochaine mise à jour du pipeline.',
  dailyChartLabel: 'Visiteurs uniques par jour — utilisez les flèches pour lire chaque jour',
  dailyShowTable: 'Afficher les chiffres par jour',
  day: 'Jour',
  seasonCol: 'Saison',
  weekShort: (n) => `Sem. ${n}`,

  // Consentements marketing
  consentsTitle: 'Consentements marketing',
  totalConsents: 'consentements au total',
  totalConsentsAll: 'consentements au total · les deux saisons',
  consentsNote: `Une saison compte les consentements donnés pendant celle-ci${NBSP}; le total campagne compte une fois le dernier choix de chaque membre.`,
  emailOnly: 'E-mail uniquement',
  emailAndSms: 'E-mail + SMS',
  smsOnly: 'SMS uniquement',
  consentTip: (label, n, pct) => `${label}${NBSP}: ${n} membres (${pct} des consentements)`,
  emailConsents: 'Consentements e-mail',
  smsConsents: 'Consentements SMS',
  ofOptIns: (pct) => `${pct} des inscriptions`,

  // Joueurs uniques par semaine
  playersPerWeek: 'Joueurs uniques par semaine',
  notStartedTip: (week) => `${week} n’a pas encore commencé`,
  playersTip: (week, n) => `${week}${NBSP}: ${n} joueurs ont terminé au moins un quiz`,
  playersNote: (n) => `Joueurs ayant terminé au moins un quiz dans la semaine · ${n} joueurs uniques sur la campagne (les deux saisons, comptés une fois)`,
  playersNoteSeason: (n, s) => `Joueurs ayant terminé au moins un quiz dans la semaine · ${n} joueurs uniques pendant la saison ${s}`,
  seasonPlayers: (s, n) => `Saison ${s} · ${n} joueurs uniques`,

  // Participations par joueur — répartition hebdomadaire
  playsDistribution: 'Participations par joueur — répartition hebdomadaire',
  plays: (n) => `${n} ${n === 1 ? 'participation' : 'participations'}`,
  weeklyMaximum: ' (maximum hebdomadaire)',
  playedTimesTip: (week, pct, n) => `${week}${NBSP}: ${pct} des joueurs de la semaine ont joué ${n}${NBSP}fois`,
  playsPerUser: (x) => `${x} par joueur`,
  distributionNote: (max) =>
    `Part des joueurs uniques de la semaine selon le nombre de quiz joués dans la semaine · chaque joueur peut jouer jusqu’à ${max}${NBSP}fois par semaine`,

  // Temps passé dans l’application
  timePerWeek: 'Temps passé dans l’application par semaine',
  noTimeTip: (week) => `${week}${NBSP}: aucun temps mesuré`,
  timeTip: (week, hours, perPlayer) => `${week}${NBSP}: ${hours} dans l’application, ${perPlayer} par joueur`,
  timeUnavailable: 'Le temps passé provient des événements analytics de l’application — pas encore disponible.',
  totalTimeInApp: 'Temps total dans l’application',
  avgTimeCampaign: 'Temps moyen par joueur (campagne)',
  perUser: (week) => `${week} · par joueur`,
  seasonTime: (n) => `Saison ${n} · temps dans l’app`,
  avgTimeSeason: (n) => `Temps moyen par joueur (saison ${n})`,

  // Récapitulatif hebdomadaire
  weeklySummary: 'Récapitulatif hebdomadaire',
  col: {
    week: 'Semaine',
    uniquePlayers: 'Joueurs uniques',
    started: 'Quiz commencés',
    plays: 'Participations',
    completion: 'Taux de complétion',
    playsPerUser: 'Participations / joueur',
    tickets: 'Tickets émis',
    timeInApp: 'Temps dans l’app',
    timePerUser: 'Temps / joueur',
  },
  campaignTotal: 'Total campagne',
  seasonTotal: (n) => `Total saison ${n}`,
  weeklySumNote:
    'Les joueurs uniques sont comptés semaine par semaine, leur somme dépasse donc le total de la saison · le total campagne compte une fois les personnes ayant joué pendant les deux saisons',

  // Exports CSV
  csvDecimal: ',',
  csvTotal: 'Total',
  csvWeekly: {
    season: 'Saison',
    week: 'Semaine',
    start: 'Début',
    end: 'Fin',
    status: 'Statut',
    uniquePlayers: 'Joueurs uniques',
    playersStarted: 'Joueurs ayant commencé',
    quizzesStarted: 'Quiz commencés',
    plays: 'Participations',
    notCompleted: 'Non terminés',
    playsPerUser: 'Participations par joueur',
    tickets: 'Tickets émis',
    timeInAppHours: 'Temps dans l’app (h)',
    timePerUserSec: 'Temps par joueur (s)',
  },
  weekStatus: { done: 'Terminée', live: 'En cours', planned: 'À venir' },
  csvWinners: {
    week: 'Semaine',
    drawDate: 'Date du tirage',
    store: 'Adresse du magasin',
    name: 'Nom affiché',
    card: 'Numéro de carte',
    prize: 'Lot (€)',
    status: 'Statut',
  },
  winnerStatus: { pending: 'En attente', approved: 'Approuvé', no_winner: 'Aucun gagnant' },

  // Gagnants et lots
  winnersSubtitle: 'Gagnants et lots · tirage au sort hebdomadaire',
  sampleData: 'Données d’exemple',
  backToDashboard: '← Retour au tableau de bord',
  winnersTitle: 'Gagnants et lots à remettre',
  winnersNote: `Tirage au sort hebdomadaire · un gagnant par semaine · lot${NBSP}: 500${NBSP}€ de crédit`,
  exportCsv: 'Exporter en CSV',
  winnersLoadError: 'Les gagnants n’ont pas pu être chargés. Réessayez dans un instant.',
  loadingWinners: 'Chargement des gagnants…',
  noDrawYet: 'Aucun tirage pour l’instant — le premier gagnant est tiré au sort à la fin de la semaine 1.',
  winnersCol: {
    weekDate: 'Semaine et date',
    store: 'Adresse du magasin',
    name: 'Nom',
    card: 'Numéro de carte',
    prize: 'Lot',
    status: 'Statut',
  },
  wk: (n) => `SEM. ${n}`,
  seasonWk: (s, n) => `S${s} · SEM. ${n}`,
  missingInfo: 'Informations manquantes',
  approved: 'Approuvé',
  noWinner: 'Aucun gagnant — personne n’avait de ticket',
  prizeCredit: (amount) => `${amount}${NBSP}€ de crédit`,
  approveWarning: 'L’approbation publie le nom et l’adresse du magasin auprès de tous les joueurs de la campagne. Ils ne pourront plus être modifiés.',
  provideAndApprove: 'Compléter et approuver',
  saveError: 'Le gagnant n’a pas pu être enregistré. Vérifiez la connexion et réessayez.',
  dialogMeta: (wk, date, card) => `${wk} · ${date} · carte ${card}`,
  storeAddress: 'Adresse du magasin',
  storePlaceholder: 'ex. Super U, 14 rue des Lilas, 44300 Nantes',
  displayName: 'Nom affiché',
  namePlaceholder: 'ex. Camille M.',
  nameHint: 'Prénom et initiale du nom',
  cancel: 'Annuler',
  saving: 'Enregistrement…',
  saveAndApprove: 'Enregistrer et approuver',
  approvalError: {
    storeRequired: 'L’adresse du magasin est obligatoire.',
    storeTooLong: `L’adresse du magasin ne peut pas dépasser ${STORE_MAX}${NBSP}caractères.`,
    storeChars: `Utilisez uniquement des lettres, des chiffres, des espaces et . , ; : ! ? ° ' ( ) / - (pas de «${NBSP}·${NBSP}»).`,
    nameRequired: 'Le nom affiché est obligatoire.',
    nameFormat: 'Indiquez le prénom et l’initiale du nom, ex. Camille M.',
  },
};

export const SURVEY_STRINGS: Record<Lang, SurveyStrings> = { en, fr };

/* ---- the chosen language -------------------------------------------------- */

const STORAGE_KEY = 'su-lang';

/** French unless the viewer picks English — the client is French. */
export const DEFAULT_LANG: Lang = 'fr';

/**
 * The language a page opens in: the viewer's last choice, else French.
 * Storage can be blocked (private windows), so every access is guarded and
 * only costs the remembered choice.
 */
export function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'fr') return saved;
  } catch {
    // storage unavailable — the default applies
  }
  return DEFAULT_LANG;
}

export function saveLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // storage unavailable — the choice lasts until the page is left
  }
}

export type SurveyI18n = { lang: Lang; t: SurveyStrings; setLang: (lang: Lang) => void };

/** The default language without a provider, so a component rendered on its own still reads. */
export const SurveyI18nContext = createContext<SurveyI18n>({ lang: DEFAULT_LANG, t: SURVEY_STRINGS[DEFAULT_LANG], setLang: () => {} });

export const useSurveyI18n = (): SurveyI18n => useContext(SurveyI18nContext);
